/* eslint-disable max-lines */

import { enPaymentMethod, enNormalizedPaymentType } from 'enums/enPaymentMethod';
import { enPaymentType } from 'enums/enPaymentType';
import { enCurrency, enLang, IProductData, IShipping, IShippingData, ICouponData } from 'interfaces/IProductData';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { paymentService } from 'services/PaymentService';
import { CACHED_MOUNT, CART_SSID, IGNORE_CACHE, TRACKER_DATA, IS_ECOMMERCE, GOOFY_ID } from 'settings';
import { round2 } from 'shared/functions';
import { Cookies } from 'react-cookie';

import { IProductConfig, IProductTemplateConfig } from '../interfaces/IProductData';
import { CACHE_ID } from '../settings';
import { cacheService } from './CacheService';
import { currencyService } from './CurrencyService';
import { warmapCache } from './InstallmentsService';
import { parameterService } from './ParameterService';
import { apiService } from 'shared/services';
import { ICartModel } from 'interfaces/ICartModel';
import { socketService } from './SocketService';
import { legacyPixelService } from 'services/LegacyPixelService';
import { translateService } from './TranslateService';
import { enProductType } from 'enums/enProductType';
import { enParameters } from 'enums/enParameters';
import { ticketService } from 'services/TicketService';
import { of } from 'rxjs/observable/of';
import { formatScripts } from 'functions';
import { scriptsService } from './ScriptsService';
import { showAlert } from 'shared/services/dialog';
import { IOrderBumpResult } from 'interfaces/IOrderBumpResult';
import { langService } from './LangService';

export interface ICartConfigReplacer {
  templateId?: number;
  template?: IProductTemplateConfig;
}

const cookie = new Cookies();

export class CartService {
  private cart$ = new ReplaySubject<IProductData>(1);

  private configReplacer$ = new BehaviorSubject<ICartConfigReplacer>({});

  private loaded = false;
  private mounted = false;

  public getCart(): Observable<IProductData> {
    return this.cart$
      .asObservable()
      .combineLatest(this.configReplacer$)
      .map(([cart, replacer]) => {
        (cart.config.template as any) = replacer.template || cart.config.template;
        (cart.config.template as any).id = Number(cart.config.template.id);
        return cart;
      })
      .map(state => {
        (state.isCached as any) = !this.mounted;
        return state;
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  public getMappedCart<T>(fn: (cart: IProductData) => T) {
    return this.getCart()
      .map(fn)
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  public getConfig(): Observable<IProductConfig> {
    return this.getCart().map(data => data.config);
  }

  public getMappedConfig<T>(fn: (config: IProductConfig) => T) {
    return this.getConfig()
      .map(fn)
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  public setShippingData(quantity: number, postalCode: string = null): Observable<IShippingData> {
    return paymentService
      .getCart()
      .combineLatest(this.getCart().filter(c => !c.isCached))
      .take(1)
      .switchMap(([payment, cart]) => {
        const body = {
          transactionId: payment.ssid,
          postalCode,
          products: [
            {
              contentId: payment.contentId,
              quantity
            }
          ]
        };

        if (cart.config.package) {
          body.products = cart.products.map(p => ({
            contentId: `${p.id}`,
            quantity: p.quantity
          }));
        }

        if (body.postalCode == 'undefined') {
          body.postalCode = null;
        }

        return apiService.post<IShippingData>('/shipping/estimate', body).loader();
      })
      .combineLatest(this.getCart().take(1))
      .map(([data, cart]) => {
        warmapCache({
          installments: data.installments,
          price: data.price
        });

        data.shippingItems.forEach(i => {
          warmapCache({
            installments: i.installments,
            price: i.totalValue
          });
        });

        const newCart: IProductData = JSON.parse(JSON.stringify(cart));
        (newCart.installments as any) = data.installments;
        (newCart.price as any) = data.price;
        (newCart.realPrice as any) = data.realPrice;
        (newCart.products as any) = data.products;
        (newCart.tax as any) = data.tax;
        (newCart.realTax as any) = data.realTax;
        (newCart.taxedPrice as any) = data.taxedPrice;
        (newCart.cartPrice as any) = data.cartPrice;

        if (newCart.config.productType == enProductType.TICKET) {
          newCart.tickets.reservationData = data.reservation;

          const hasError = cart.tickets.reservationData.products.some(p => p.success == false);

          if (hasError) {
            // tslint:disable-next-line:max-line-length
            showAlert(
              'Infelizmente a quantidade de tickets selecionada é maior que o nosso estoque atual. Tente novamente mais tarde.',
              'Ops!'
            ).subscribe();
          }
        }

        this.cart$.next(newCart);

        if (newCart.tickets) {
          ticketService.setTimeLeft(newCart.tickets.reservationData.expiresIn);
        }

        paymentService.setData({
          quantity: data.products[0].quantity
        });

        paymentService.setPaymentData(0, {
          value: data.price,
          installments: data.installments,
          numberInstallments: data.installments.length
        });

        return data;

        this.setShipping(data.hash, data.shippingItems[0]);

        return data;
      });
  }

  public setShipping(hash: string, selectedShipping: IShipping): void {
    this.getCart()
      .take(1)
      .map(cart => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));

        (newCart.price as any) = selectedShipping.totalValue;
        (newCart.taxedPrice as any) = selectedShipping.taxedPrice;
        (newCart.tax as any) = selectedShipping.tax;
        (newCart.cartPrice as any) = selectedShipping.cartPrice;
        (newCart.realPrice as any) = selectedShipping.realPrice;
        (newCart.installments as any) = selectedShipping.installments;

        this.cart$.next(newCart);
        paymentService.setData({
          shipping: {
            code: selectedShipping.code,
            hash
          }
        });
        paymentService.setPaymentData(0, {
          value: selectedShipping.totalValue,
          installments: selectedShipping.installments,
          numberInstallments: selectedShipping.installments.length
        });

        return true;
      })
      .subscribe();
  }

  public setOrderBumps(products: string[]): Observable<any> {
    return this.getCart()
      .take(1)
      .switchMap(cart => {
        return Observable.combineLatest(
          Observable.of(cart),
          apiService.post<IOrderBumpResult>(`/bump/${cart.transactionKey}/${cart.contentId}`, { products })
        );
      })
      .map(([cart, bumpData]) => {
        const newCart = JSON.parse(JSON.stringify(cart));

        const mergedCart = { ...newCart, ...bumpData };
        mergedCart.installments = bumpData.installments;

        this.cart$.next(mergedCart);
        paymentService.setPaymentData(0, {
          value: bumpData.price,
          installments: bumpData.installments
        });

        return true;
      });
  }

  public setCoupon(coupon: string, couponType: number = 1): Observable<boolean> {
    return this.getCart()
      .filter(cart => !!cart.transactionKey)
      .take(1)
      .switchMap(cart => {
        const url =
          couponType === 1
            ? `/coupon/${cart.transactionKey}/product/${cart.contentId}`
            : `/coupon/${cart.transactionKey}/product/${cart.contentId}/modal`;

        return apiService.post<ICouponData>(url, {
          coupon
        });
      })
      .switchMap(coupon => this.updateCouponData(coupon));
  }

  public enableAskAddress(): Observable<IProductData> {
    return this.getCart()
      .take(1)
      .filter(cart => cart.config.askAddressAfterPayment || cart.config.askAddressBeforePayment)
      .do(cart => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));

        (newCart.config.askAddressAfterPayment as any) = false;
        (newCart.config.askAddressBeforePayment as any) = true;

        this.cart$.next(newCart);
      });
  }

  public removeCoupon(): Observable<boolean> {
    return this.getCart()
      .take(1)
      .switchMap(cart => {
        return apiService.del<ICouponData>(`/coupon/${cart.transactionKey}/product/${cart.contentId}`);
      })
      .switchMap(coupon => this.updateCouponData(coupon));
  }

  public updateCouponData(coupon: ICouponData): Observable<boolean> {
    return Observable.of(coupon)
      .combineLatest(this.getCart().take(1), paymentService.getCart().take(1))
      .map(([coupon, cart, payment]) => {
        const newCart = this.createNewCart(cart, coupon);

        // REMOVER APÓS CORRIGIDO NA API
        if (newCart.config.paymentType === enPaymentType.SUBSCRIPTION) {
          (newCart.subscription.tax as any) = coupon.price;
        }

        warmapCache(newCart);

        this.cart$.next(newCart);

        const newValue = round2(newCart.price / payment.payment.length);

        let lastPaymentValue = newValue + (newCart.price - newValue * payment.payment.length);

        lastPaymentValue = round2(lastPaymentValue);

        payment.payment.forEach((p, key) => {
          const selectedInstallment = p.numberInstallments;

          const maxInstallments =
            newCart.config.maxInstallments < newCart.installments.length ? newCart.config.maxInstallments : newCart.installments.length;

          const numberInstallments = maxInstallments >= selectedInstallment ? selectedInstallment : maxInstallments;

          paymentService.setPaymentData(key, {
            value: key != payment.payment.length - 1 ? newValue : lastPaymentValue,
            installments: newCart.installments,
            numberInstallments
          });
        });

        return true;
      })
      .logError();
  }

  public disableOneClickBuy(): Observable<void> {
    cookie.remove('he');
    cookie.remove('email');

    return this.getCart()
      .take(1)
      .map(cart => {
        let newCart: IProductData = JSON.parse(JSON.stringify(cart));
        (newCart.config.isOneClick as any) = false;
        this.cart$.next(newCart);

        return null;
      });
  }

  public setConfig(name: string, value: any) {
    const config = JSON.parse(JSON.stringify(this.configReplacer$.value));

    config[name] = value;

    this.configReplacer$.next(config);
  }

  public getCurrentState(): Observable<IProductData> {
    return this.getCart().first();
  }

  public loadContent(contentId: string, lang: enLang, currency: enCurrency): Observable<boolean> {
    let transactionKey = CART_SSID;

    const cachedMount = CACHED_MOUNT;

    let isEcommerce = false;
    let isRec = false;

    let loadObservable = parameterService
      .getAll()
      .take(1)
      .switchMap(parameters => {
        if (cachedMount) {
          return of(parameters).delay(3000);
        }
        return of(parameters);
      })
      .switchMap(parameters => {
        if (IS_ECOMMERCE || !!`${contentId}`.match(/^c_/)) {
          contentId = contentId.replace(/^c_/, '');
          parameters = { ...parameters, ec: '1' };
          isEcommerce = true;
        }

        if (!!`${contentId}`.match(/^r_/)) {
          contentId = contentId.replace(/^r_/, '');
          parameters = { ...parameters, rec: '1' };
          isRec = true;
        }

        const cookie = new Cookies();

        const lastPurchase = cookie.get('last_purchase');
        if (parameters.u && lastPurchase) {
          delete parameters.u;
          parameters.upsell = true;

          const [paymentMethod, newTransactionKey] = lastPurchase.split(':');

          if (paymentMethod == enNormalizedPaymentType.BANKSLIP) {
            transactionKey = newTransactionKey;
          }
        }

        return apiService.put<IProductData>(`/cart/${transactionKey}`, {
          contentId,
          currency,
          lang,
          parameters,
          tracker: TRACKER_DATA,
          goofyId: GOOFY_ID
        });
      })
      .catch(err => {
        if (err.code === 'TRA_A') {
          cookie.remove(`goofy-${contentId}`, {
            path: '/',
            domain: 'eduzz.com'
          });
          window.location.reload();
          return Observable.empty();
        }

        return Observable.throw(err);
      })
      .map((data: IProductData) => {
        (data.config.scripts as any) = formatScripts(data.config.scripts);
        (data.isCached as any) = false;

        scriptsService.setScripts(data.config.scripts);

        if (window.TEMPLATE_CONFIG) {
          const templateConfig: any = window.TEMPLATE_CONFIG;

          (data.config.template as any) = {
            id: templateConfig.template,
            inputStyle: 'normal',
            styles: templateConfig,
            media: {
              bannerCenter: templateConfig.bannerCenter,
              bannerTop: templateConfig.bannerTop,
              bannerRight: templateConfig.bannerRight,
              bannerYoutube: templateConfig.bannerYoutube
            }
          };
        }

        return data;
      });

    if (!this.loaded) {
      this.loaded = true;
      loadObservable = loadObservable.startWith(cachedMount);
    }

    const observable = loadObservable
      .filter(data => !!data)
      .combineLatest(paymentService.getCart().take(1))
      .map(([data, paymentData]) => {
        if (!parameterService.getSync(enParameters.pu)) {
          parameterService.setParameter(enParameters.pu, `${data.config.maxInstallments}`);
        }

        data.originalContentId = contentId;

        let installments = data.installments;

        if (data.upsell) {
          const oneX = installments.find(x => x.installment === 1);
          installments = [oneX];
        }

        (data.installments as any) = installments;

        socketService.emit('cart', {
          contentId: data.contentId,
          producerId: data.config.producerId,
          transactionKey
        });

        warmapCache(data);

        if (!IGNORE_CACHE && !data.isCached && !data.user && !data.upsell) {
          cacheService.setCache(CACHE_ID, data);
        }

        if (!data.isCached) {
          this.mounted = true;
        }

        const lang = data.config.lang || enLang.PT_BR;

        /*
          legacyPixelService: Should be removed in not too distance future
        */
        legacyPixelService.setPixel({ ...data.config.pixel, contentId });

        langService.setLang(lang);
        currencyService.setCurrencyByLang(lang);
        translateService.setLang(lang);

        if (!paymentData.contentId) {
          this.initializePayment(data.contentId, transactionKey, data, paymentData);
        }

        paymentService.loadParameters();

        if (data.upsell) {
          paymentService.setPaymentData(0, {
            paymentMethod: data.upsell,
            numberInstallments: 1
          });
          this.initializePayment(isEcommerce || isRec ? data.contentId : data.originalContentId, transactionKey, data, paymentData);
        }

        if (!data.config.enableCreditcard) {
          let pm = null;

          if (data.config.enablePaypal) {
            pm = enPaymentMethod.PAYPAL;
          }

          if (data.config.enablePaypal && lang !== enLang.PT_BR) {
            pm = enPaymentMethod.PAYPAL;
          }

          if (data.config.enableBankslip && lang != enLang.EN_US) {
            pm = enPaymentMethod.BOLETO;
          }

          if (data.config.paymentType === enPaymentType.FREE) {
            pm = enPaymentMethod.FREE;
          }

          if (pm) {
            paymentService.setPaymentData(0, {
              paymentMethod: pm
            });
          }

          if (!pm) {
            (data.config.enableCreditcard as any) = true;
          }
        }

        if (data.tickets) {
          ticketService.setTimeLeft(data.tickets.reservationData.expiresIn);
          ticketService.setReserveTime(data.tickets.reservationData.reserveTime);
        }

        this.cart$.next(data);

        if (data.next) {
          if (!data.next.plans) {
            paymentService.setData({ next: { withoutLimitation: true } });
          }
        }

        paymentService.setPaymentData(0, { installments });

        if (data.upsell) {
          console.log('setted to: ', data.upsell);
          paymentService.setPaymentData(0, {
            paymentMethod: data.upsell == 'boleto' ? enPaymentMethod.BOLETO : data.upsell,
            numberInstallments: 1
          });
        }

        if (data.installmentSelected > 0) {
          paymentService.setDefaultInstallment(data.installmentSelected);
        }

        return true;
      })
      .logError();

    return translateService.loadTranslations().switchMap(() => observable);
  }

  public setCart = (cart: IProductData, propriety: any) => {
    const newCart = {
      ...cart,
      ...propriety
    };

    this.cart$.next(newCart);
  };

  private createNewCart(cart: IProductData, coupon: ICouponData) {
    let newCart: IProductData = JSON.parse(JSON.stringify(cart));

    let cartCoupon = null;

    if (coupon.coupon) {
      cartCoupon = {
        code: coupon.coupon.code,
        value: coupon.coupon.value
      };
    }

    newCart = {
      ...newCart,
      price: coupon.price,
      realPrice: coupon.realPrice,
      tax: coupon.tax,
      cartPrice: coupon.cartPrice,
      realTax: cart.realTax,
      taxedPrice: coupon.taxedPrice,
      coupon: cartCoupon,
      numberInstallments: coupon.installments.length,
      installments: coupon.installments,
      products: coupon.products,
      config: {
        ...newCart.config,
        maxInstallments: cart.config.maxInstallments || coupon.installments.length
      }
    };

    return newCart;
  }

  private initializePayment(contentId: string, ssid: string, data: IProductData, paymentData: ICartModel) {
    paymentService.setData({
      contentId,
      ssid,
      fullPrice: Number(data.price.toFixed(2)),
      quantity: data.products[0].quantity,
      productType: data.config.productType
    });

    paymentService.setPaymentData(0, {
      paymentMethod:
        data.config.paymentType == enPaymentType.FREE ? enPaymentMethod.FREE : data.config.isOneClick ? enPaymentMethod.ONE_CLICK_BUY : null,
      installments: data.installments,
      numberInstallments: data.numberInstallments,
      value: data.price
    });

    if (data.user) {
      const user = data.user || {};
      paymentService.setData({
        name: paymentData.name || user.name,
        email: paymentData.email || user.email,
        phone: paymentData.phone || user.phone,
        document: paymentData.document || user.document
      });

      const currentAddress = paymentData.address || {};

      if (user.postalCode) {
        paymentService.setAddressData({
          postalCode: currentAddress.postalCode || user.postalCode,
          street: currentAddress.street || user.street,
          number: currentAddress.number || user.number,
          district: currentAddress.district || user.district,
          complement: currentAddress.complement || user.complement,
          province: currentAddress.province || user.province,
          cityId: currentAddress.cityId || user.cityId.toString(),
          city: currentAddress.city || user.city
        });
      }

      if (user.creditCard) {
        paymentService.setPaymentData(0, {
          creditCardId: user.creditCard.id,
          brand: user.creditCard.brand
        });
      }
    }
  }
}

// tslint:disable-next-line:variable-name
export const CartServiceFactory = () => new CartService();

export const cartService = CartServiceFactory();
